<template>
    <component
        :is="PanelComponent"
        :searchType="searchType"
        :searchSetting="initSetting"
        :images="settingImages"
        v-bind="panelProps"
        @reset="handleReset"
    >
        <BioSearchSetting
            v-if="!isShort"
            :searchType="BIO_SEARCH_TYPE.short"
            storeKey="upload-short"
            :sequenceType="sequenceType"
            :dbType="dbType"
        />
    </component>
</template>

<script>
import { defineComponent } from 'vue';
import {
    AssistantPreset,
    ShortPreset,
    getShortDefaultAlgorithm,
    getDefaultAlgorithm,
    getDefaultSettings,
} from '@patsnap-biz/bio-search-setting';
import { BIO_SEARCH_TYPE } from '@/constants';
import GlobalAlignmentAssistantPreset from './search-setting/global-alignment-assistant-preset.vue';
import { registerNwFieldConfigs } from './search-setting/nw-field-configs';
import {
    isNwSearchSetting,
    normalizeManualSearchSetting,
} from '../utils/global-alignment';
import 'element-ui-2/lib/theme-chalk/icon.css';
import '@patsnap-ui/icon/assets/solid/info.svg';
import '@patsnap-ui/icon/assets/solid/warning.svg';
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/DropdownRight.svg';

import ungappedExplanationImgEn from '../../../assets/images/bio/preferences_propver_en.png';
import ungappedExplanationImgCn from '../../../assets/images/bio/preferences_propver_cn.png';
import similarityExplanationCn from '../../../assets/images/bio/explanation_cn.png';
import similarityExplanationEn from '../../../assets/images/bio/explanation_en.png';
import globalAlignmentDescription1 from '../../../assets/images/bio/global-alignment-description-1.jpg';
import globalAlignmentDescription2 from '../../../assets/images/bio/global-alignment-description-2.jpg';
import globalAlignmentTipCn from '../../../assets/images/bio/global-alignment-tip-cn.png';
import globalAlignmentTipEn from '../../../assets/images/bio/global-alignment-tip-en.png';
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';

registerNwFieldConfigs();

export default defineComponent({
    name: 'BioSearchSetting',
    components: {
        AssistantPreset,
        GlobalAlignmentAssistantPreset,
        ShortPreset,
    },
    props: {
        searchType: {
            type: String,
            required: true,
        },
        storeKey: {
            type: String,
            required: true,
        },
        sequenceType: {
            type: String,
            required: true,
        },
        dbType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            algorithmSettings: null,
        };
    },
    computed: {
        ...mapGetters({
            bioSearchSettingLists: 'bio/searchSettingLists',
            hasGlobalAlignmentAlgorithm: 'bio/hasGlobalAlignmentAlgorithm',
        }),
        hasGlobalAlignment() {
            return this.hasGlobalAlignmentAlgorithm === true;
        },
        isManual() {
            return this.searchType === BIO_SEARCH_TYPE.sequence;
        },
        isShort() {
            return this.searchType === BIO_SEARCH_TYPE.short;
        },
        settingImages() {
            const isCn = this.$i18n.locale === 'cn';
            return {
                similarityExplanation: isCn ? similarityExplanationCn : similarityExplanationEn,
                ungappedExplanation: isCn ? ungappedExplanationImgCn : ungappedExplanationImgEn,
                globalAlignmentExplanation: {
                    1: globalAlignmentDescription1,
                    2: globalAlignmentDescription2,
                },
                globalAlignmentSimilarityExplanation: isCn
                    ? globalAlignmentTipCn
                    : globalAlignmentTipEn,
            };
        },
        PanelComponent() {
            if (this.isShort) return ShortPreset;
            if (this.isManual && this.hasGlobalAlignment) {
                return GlobalAlignmentAssistantPreset;
            }
            return AssistantPreset;
        },
        BIO_SEARCH_TYPE() {
            return BIO_SEARCH_TYPE;
        },
        initSetting() {
            let setting = this.bioSearchSettingLists[this.storeKey] || {};
            if (_.isEmpty(setting)) {
                setting = _.cloneDeep(this.defaultSetting);
                this['bio/setSearchSetting']({
                    key: this.storeKey,
                    value: setting,
                });
            }
            const normalized = this.normalizeSetting(setting, true);
            return this.appendAlgorithmSettings(normalized);
        },
        defaultSetting() {
            const setting = {
                ..._.cloneDeep(getDefaultSettings(this.searchType)),
                ...this.externalSetting,
            };
            return this.appendAlgorithmSettings(this.normalizeSetting(setting));
        },
        externalSetting() {
            return {
                sequenceType: this.sequenceType,
                dbType: this.dbType,
            };
        },
        externalConfigNames() {
            return ['sequenceType', 'dbType'];
        },
        panelProps() {
            if (!this.isShort) {
                return {};
            }
            return {
                externalConfigNames: this.externalConfigNames,
                externalSetting: this.externalSetting,
            };
        },
    },
    methods: {
        ...mapActions(['bio/setSearchSetting']),
        normalizeSetting(setting, persist = false) {
            if (!this.isManual) return setting;

            const normalized = normalizeManualSearchSetting(
                setting,
                this.hasGlobalAlignment,
            );
            if (_.isEqual(setting, normalized)) return setting;

            if (persist) {
                this['bio/setSearchSetting']({
                    key: this.storeKey,
                    value: normalized,
                });
            }
            return normalized;
        },
        appendAlgorithmSettings(setting) {
            if (this.isManual && isNwSearchSetting(setting)) {
                return setting;
            }
            const algorithmSettings = _.defaults(
                setting?.algorithmSettings || {},
                this.isShort
                    ? getShortDefaultAlgorithm(this.sequenceType, this.dbType) :
                    getDefaultAlgorithm(this.sequenceType, this.dbType),
            );
            return Object.assign(setting, {
                algorithmSettings,
            });
        },
        handleReset() {
            const resetSetting = {};
            Object.assign(resetSetting, _.cloneDeep(this.defaultSetting));
            this['bio/setSearchSetting']({
                key: this.storeKey,
                value: resetSetting,
            });
            this.$emit('reset');
        },
    },
});
</script>

<style lang="scss">
.patsnap-biz-bio_search_setting {
    .search-preference__alg-label,
    .search-preferences--section__title {
        display: flex;
        align-items: center;
    }

    .search-preference__title,
    .search-preference__title > div {
        display: inline-flex;
        align-items: center;
    }

    .search-preferences--value {
        line-height: 0;
    }

    .el-popover__reference-wrapper {
        display: inline-flex;
        align-items: center;
        line-height: 0;
        vertical-align: middle;

        .patsnap-biz-icon {
            margin-bottom: 0;
            margin-left: 0 !important;
        }
    }
}

</style>
